import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// SMART-AUTOMATIONS Etapa 8 — toggles individuais por automação.
// Defaults razoáveis (todas ON exceto auto-setup que precisa gatilho).
// auto_setup_categories é gerenciado por outra UI (chip "Quer ativar?"
// que aparece após 2º aceite — Etapa 2).
const AUTOMATIONS = [
  {
    key: "automation_duplicate_check",
    title: "Detecção de duplicata",
    desc: "Avisa antes de salvar despesa similar a uma dos últimos 3 dias.",
  },
  {
    key: "automation_auto_category",
    title: "Auto-categorização",
    desc: "Sugere categoria baseado no histórico da descrição.",
  },
  {
    key: "automation_anomaly_alert",
    title: "Alerta de aumento de gasto",
    desc: "Notifica quando uma categoria estoura a média de 3 meses.",
  },
  {
    key: "automation_weekly_summary",
    title: "Resumo semanal",
    desc: "Toda segunda-feira 9h, gera resumo da semana e cria nota.",
  },
];

export default function AutomationsSettings() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const r = await authFetch(`${API_URL}/api/preferences`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!abort) setPrefs(data);
      } catch (err) {
        console.error(err);
        toast.error("Não consegui carregar suas preferências");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, []);

  const _patch = useCallback(async (body) => {
    setSaving(true);
    try {
      const r = await authFetch(`${API_URL}/api/preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setPrefs(data);
    } catch (err) {
      console.error(err);
      toast.error("Não consegui salvar agora");
    } finally {
      setSaving(false);
    }
  }, []);

  const onToggle = (key) => (next) => {
    setPrefs((p) => ({ ...(p || {}), [key]: next }));
    _patch({ [key]: next });
  };

  const onThresholdCommit = (vals) => {
    const v = (vals?.[0] ?? 1.25);
    setPrefs((p) => ({ ...(p || {}), automation_anomaly_threshold: v }));
    _patch({ automation_anomaly_threshold: v });
  };

  if (loading || !prefs) {
    return <div className="text-caption text-foreground-subtle">Carregando preferências…</div>;
  }

  const threshold = prefs.automation_anomaly_threshold ?? 1.25;
  const thresholdPct = Math.round((threshold - 1) * 100);

  return (
    <section className="space-y-6" data-testid="automations-settings">
      <div>
        <h2 className="text-h2 text-foreground mb-1">Automações</h2>
        <p className="text-caption text-foreground-subtle">
          O Nexo executa estas automações em segundo plano. Cada uma pode ser
          desativada individualmente.
        </p>
      </div>

      <ul className="space-y-3">
        {AUTOMATIONS.map((a) => {
          const enabled = prefs[a.key] !== false;
          return (
            <li
              key={a.key}
              className="flex items-start justify-between gap-4 p-4 bg-surface-card border border-subtle rounded-xl"
            >
              <div className="flex-1 min-w-0">
                <div className="text-body text-foreground">{a.title}</div>
                <div className="text-caption text-foreground-muted mt-1">{a.desc}</div>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={onToggle(a.key)}
                disabled={saving}
                data-testid={`automation-toggle-${a.key}`}
              />
            </li>
          );
        })}
      </ul>

      {prefs.automation_anomaly_alert !== false && (
        <div className="p-4 bg-surface-card border border-subtle rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="text-body text-foreground">Sensibilidade do alerta</div>
            <div className="text-caption text-brand-cyan">
              {thresholdPct}% acima da média
            </div>
          </div>
          <p className="text-caption text-foreground-subtle mb-4">
            Defina a partir de quanto acima da média o Nexo te alerta.
            Mais sensível = mais alertas; menos sensível = só os grandes saltos.
          </p>
          <Slider
            value={[threshold]}
            min={1.10}
            max={1.50}
            step={0.05}
            onValueCommit={onThresholdCommit}
            disabled={saving}
            data-testid="automation-anomaly-slider"
          />
          <div className="flex justify-between text-caption text-foreground-subtle mt-2">
            <span>+10%</span>
            <span>+25% (padrão)</span>
            <span>+50%</span>
          </div>
        </div>
      )}

      {Array.isArray(prefs.auto_setup_categories) && prefs.auto_setup_categories.length > 0 && (
        <div className="p-4 bg-surface-card border border-subtle rounded-xl">
          <div className="text-body text-foreground mb-2">
            Auto-setup ativado para
          </div>
          <div className="flex flex-wrap gap-2">
            {prefs.auto_setup_categories.map((cat) => (
              <span
                key={cat}
                className="px-3 py-1 text-caption bg-brand-cyan/10 text-brand-cyan rounded-full"
              >
                {cat}
              </span>
            ))}
          </div>
          <p className="text-caption text-foreground-subtle mt-3">
            Para essas categorias, ao criar um projeto o Nexo já configura
            tarefas, lembretes e pesquisa automaticamente.
          </p>
        </div>
      )}
    </section>
  );
}
